'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { PluginConfig, PluginModuleRef } from '../types/app-config';

export type PluginType =
  | 'fieldRenderers'
  | 'columnRenderers'
  | 'actionButtons'
  | 'sidebarWidgets';

type PluginIndex = Record<PluginType, Record<string, PluginModuleRef>>;

interface PluginRegistryContextValue {
  hasPlugin: (type: PluginType, name?: string) => boolean;
  getCachedPlugin: <T = unknown>(type: PluginType, name?: string) => T | null;
  loadPlugin: <T = unknown>(type: PluginType, name?: string) => Promise<T | null>;
  listPluginNames: (type: PluginType) => string[];
}

function createEmptyPluginIndex(): PluginIndex {
  return {
    fieldRenderers: {},
    columnRenderers: {},
    actionButtons: {},
    sidebarWidgets: {},
  };
}

function normalizeName(name: string | undefined): string | undefined {
  if (!name) return undefined;
  const trimmed = name.trim();
  return trimmed ? trimmed : undefined;
}

function buildPluginIndex(plugins: PluginConfig[] | undefined): PluginIndex {
  const index = createEmptyPluginIndex();
  if (!plugins?.length) {
    return index;
  }

  plugins.forEach(plugin => {
    if (plugin.fieldRenderers) {
      Object.assign(index.fieldRenderers, plugin.fieldRenderers);
    }
    if (plugin.columnRenderers) {
      Object.assign(index.columnRenderers, plugin.columnRenderers);
    }
    if (plugin.actionButtons) {
      Object.assign(index.actionButtons, plugin.actionButtons);
    }
    if (plugin.sidebarWidgets) {
      Object.assign(index.sidebarWidgets, plugin.sidebarWidgets);
    }
  });

  return index;
}

function resolvePluginExport(moduleNamespace: Record<string, unknown>, moduleRef: PluginModuleRef): unknown {
  if (moduleRef.exportName) {
    return moduleNamespace[moduleRef.exportName];
  }

  if ('default' in moduleNamespace) {
    return moduleNamespace.default;
  }

  const candidateEntry = Object.entries(moduleNamespace)
    .find(([key, value]) => key !== '__esModule' && value !== undefined);
  return candidateEntry ? candidateEntry[1] : undefined;
}

function pluginCacheKey(type: PluginType, name: string): string {
  return `${type}:${name}`;
}

const PluginRegistryContext = createContext<PluginRegistryContextValue | null>(null);

export interface PluginRegistryProviderProps {
  children: ReactNode;
  plugins?: PluginConfig[];
}

export function PluginRegistryProvider({ children, plugins }: PluginRegistryProviderProps) {
  const pluginIndex = useMemo(
    () => buildPluginIndex(plugins),
    [plugins],
  );
  const cacheRef = useRef(new Map<string, unknown>());
  const pendingLoadsRef = useRef(new Map<string, Promise<unknown | null>>());

  const getModuleRef = useCallback((
    type: PluginType,
    name: string | undefined,
  ): PluginModuleRef | null => {
    const normalizedName = normalizeName(name);
    if (!normalizedName) return null;

    const moduleRef = pluginIndex[type][normalizedName];
    return moduleRef || null;
  }, [pluginIndex]);

  const hasPlugin = useCallback((type: PluginType, name?: string) => (
    !!getModuleRef(type, name)
  ), [getModuleRef]);

  const getCachedPlugin = useCallback(<T,>(type: PluginType, name?: string): T | null => {
    const normalizedName = normalizeName(name);
    if (!normalizedName) return null;

    const cacheKey = pluginCacheKey(type, normalizedName);
    if (!cacheRef.current.has(cacheKey)) {
      return null;
    }
    return cacheRef.current.get(cacheKey) as T;
  }, []);

  const loadPlugin = useCallback(async <T,>(type: PluginType, name?: string): Promise<T | null> => {
    const normalizedName = normalizeName(name);
    const moduleRef = getModuleRef(type, normalizedName);
    if (!normalizedName || !moduleRef) {
      return null;
    }

    const cacheKey = pluginCacheKey(type, normalizedName);
    if (cacheRef.current.has(cacheKey)) {
      return cacheRef.current.get(cacheKey) as T;
    }

    if (pendingLoadsRef.current.has(cacheKey)) {
      return pendingLoadsRef.current.get(cacheKey) as Promise<T | null>;
    }

    const loadPromise = (async () => {
      try {
        const namespace = await import(/* @vite-ignore */ moduleRef.module) as Record<string, unknown>;
        const resolved = resolvePluginExport(namespace, moduleRef);
        if (resolved === undefined || resolved === null) {
          throw new Error(
            `Plugin "${normalizedName}" did not export a value from module "${moduleRef.module}".`,
          );
        }
        cacheRef.current.set(cacheKey, resolved);
        return resolved as T;
      } catch (error) {
        if (typeof console !== 'undefined') {
          // eslint-disable-next-line no-console
          console.error(
            `[runtime-engine] Failed to load plugin "${normalizedName}" for ${type}.`,
            error,
          );
        }
        return null;
      } finally {
        pendingLoadsRef.current.delete(cacheKey);
      }
    })();

    pendingLoadsRef.current.set(cacheKey, loadPromise);
    return loadPromise;
  }, [getModuleRef]);

  const listPluginNames = useCallback((type: PluginType): string[] => (
    Object.keys(pluginIndex[type])
  ), [pluginIndex]);

  const value = useMemo<PluginRegistryContextValue>(() => ({
    hasPlugin,
    getCachedPlugin,
    loadPlugin,
    listPluginNames,
  }), [hasPlugin, getCachedPlugin, loadPlugin, listPluginNames]);

  return (
    <PluginRegistryContext.Provider value={value}>
      {children}
    </PluginRegistryContext.Provider>
  );
}

function usePluginRegistry(): PluginRegistryContextValue {
  const context = useContext(PluginRegistryContext);
  if (!context) {
    throw new Error('usePlugin must be used within a PluginRegistryProvider');
  }
  return context;
}

export interface UsePluginResult<T> {
  plugin: T | null;
  isLoading: boolean;
  error: Error | null;
}

export function usePlugin<T = unknown>(
  type: PluginType,
  name?: string,
): UsePluginResult<T> {
  const { hasPlugin, getCachedPlugin, loadPlugin } = usePluginRegistry();
  const normalizedName = normalizeName(name);

  const [result, setResult] = useState<UsePluginResult<T>>(() => {
    if (!normalizedName || !hasPlugin(type, normalizedName)) {
      return { plugin: null, isLoading: false, error: null };
    }

    const cached = getCachedPlugin<T>(type, normalizedName);
    if (cached) {
      return { plugin: cached, isLoading: false, error: null };
    }
    return { plugin: null, isLoading: true, error: null };
  });

  useEffect(() => {
    if (!normalizedName || !hasPlugin(type, normalizedName)) {
      setResult({ plugin: null, isLoading: false, error: null });
      return;
    }

    const cached = getCachedPlugin<T>(type, normalizedName);
    if (cached) {
      setResult({ plugin: cached, isLoading: false, error: null });
      return;
    }

    let cancelled = false;
    setResult(prev => ({ ...prev, isLoading: true, error: null }));

    void loadPlugin<T>(type, normalizedName)
      .then(plugin => {
        if (cancelled) return;
        setResult({
          plugin,
          isLoading: false,
          error: plugin ? null : new Error(`Plugin "${normalizedName}" could not be resolved.`),
        });
      })
      .catch(error => {
        if (cancelled) return;
        setResult({
          plugin: null,
          isLoading: false,
          error: error instanceof Error ? error : new Error('Failed to load plugin.'),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [type, normalizedName, hasPlugin, getCachedPlugin, loadPlugin]);

  return result;
}

export function usePluginNames(type: PluginType): string[] {
  const { listPluginNames } = usePluginRegistry();
  return useMemo(
    () => listPluginNames(type),
    [listPluginNames, type],
  );
}
